/**
 * Fare Calculator Algorithm — v2
 * Improvements:
 *  #1 Time-based duration pricing
 *  #2 Fixed rate naming (multiplier vs percentage, clearly labelled)
 *  #3 Minimum fare per vehicle type
 *  #4 Round-trip multiplier actually applied
 *  #5 Surge/demand pricing hook
 *  #6 Config validation on startup
 */

// ─────────────────────────────────────────────
// VEHICLE RATE CONFIGURATIONS
// ─────────────────────────────────────────────

/**
 * NAMING FIX (#2):
 *  - `maintenanceMultiplier` / `driverAllowanceMultiplier`:
 *      Applied as  fuelCost × multiplier  (e.g. 1.6 → 160% of fuel cost).
 *      Previously mislabelled "rate" with unit "Percentage", causing confusion.
 *  - `serviceChargeRate`: true percentage expressed as decimal (0.08 = 8%).
 *  - `perMinuteRate`: new — earnings per minute of trip duration (#1).
 *  - `minimumFare`: floor applied after all calculations (#3).
 */
import axios from "axios";
//  const axios = require('/axios');

const VEHICLE_CONFIGS = {
  /** Large vehicle – e.g. van / SUV */
  large: {
    fuelPrice: 88.8,
    vehicleCost: 2_200_000,
    depreciationRatePerYear: 10,
    insurancePerYear: 40_000,
    averageTripsPerMonth: 20,
    mileage: 9,
    maintenanceMultiplier: 1.6, // #2: was "maintenanceRate" – applied as × multiplier
    driverAllowanceMultiplier: 1.6, // #2: was "driverAllowanceRate" – same
    serviceChargeRate: 0.08, // true % decimal: 8% of subtotal → company revenue
    timeFactor: 1.3,
    perMinuteRate: 8, // #1: pesos (or local currency) per minute of trip
    minimumFare: 500, // #3: floor fare in local currency
  },

  /** Mid-size vehicle */
  medium: {
    fuelPrice: 88.8,
    vehicleCost: 1_400_000,
    depreciationRatePerYear: 15,
    insurancePerYear: 30_000,
    averageTripsPerMonth: 20,
    mileage: 10,
    maintenanceMultiplier: 1.6,
    driverAllowanceMultiplier: 1.6,
    serviceChargeRate: 0.08,
    timeFactor: 1.3,
    perMinuteRate: 5, // #1
    minimumFare: 200, // #3
  },

  /** Small / economy vehicle */
  small: {
    fuelPrice: 97.7,
    vehicleCost: 500_000,
    depreciationRatePerYear: 15,
    insurancePerYear: 20_000,
    averageTripsPerMonth: 20,
    mileage: 14,
    maintenanceMultiplier: 1.6,
    driverAllowanceMultiplier: 1.6,
    serviceChargeRate: 0.08,
    timeFactor: 1.3,
    perMinuteRate: 3, // #1
    minimumFare: 100, // #3
  },
};

// ─────────────────────────────────────────────
// CONFIG VALIDATION  (#6)
// Runs once at startup — throws early if any
// vehicle config is missing required fields.
// ─────────────────────────────────────────────
const REQUIRED_CONFIG_FIELDS = [
  "fuelPrice",
  "vehicleCost",
  "depreciationRatePerYear",
  "insurancePerYear",
  "averageTripsPerMonth",
  "mileage",
  "maintenanceMultiplier",
  "driverAllowanceMultiplier",
  "serviceChargeRate",
  "timeFactor",
  "perMinuteRate",
  "minimumFare",
];

function validateConfigs(configs) {
  for (const [type, cfg] of Object.entries(configs)) {
    for (const field of REQUIRED_CONFIG_FIELDS) {
      if (cfg[field] === undefined || cfg[field] === null) {
        throw new Error(
          `[fareCalculator] Vehicle config "${type}" is missing required field: "${field}"`,
        );
      }
      if (typeof cfg[field] !== "number" || isNaN(cfg[field])) {
        throw new Error(
          `[fareCalculator] Vehicle config "${type}.${field}" must be a valid number. Got: ${cfg[field]}`,
        );
      }
      if (cfg[field] < 0) {
        throw new Error(
          `[fareCalculator] Vehicle config "${type}.${field}" must be non-negative. Got: ${cfg[field]}`,
        );
      }
    }
  }
}

// Validate immediately on module load
validateConfigs(VEHICLE_CONFIGS);

// ─────────────────────────────────────────────
// SURGE PRICING  (#5)
// ─────────────────────────────────────────────

/**
 * Default surge evaluator — always returns 1.0 (no surge).
 *
 * Replace or extend this with your own logic, e.g.:
 *   - Time-of-day rules (rush hour, late night)
 *   - Demand/supply ratio from your DB
 *   - Weather or event-based multipliers
 *   - External API call to a pricing service
 *
 * @param {object} context
 * @param {string} context.vehicleType
 * @param {Date}   context.bookingTime
 * @param {object} context.origin        - { lat, lng }
 * @param {object} context.destination   - { lat, lng }
 * @returns {number} Multiplier >= 1.0
 */
async function getSurgeMultiplier({
  vehicleType,
  bookingTime,
  origin,
  destination,
}) {
  // ── Example rule set (uncomment and adapt as needed) ──────────────────────
  //
  // const hour = bookingTime.getHours();
  // const isRushHour = (hour >= 7 && hour <= 9) || (hour >= 17 && hour <= 20);
  // const isLateNight = hour >= 23 || hour < 5;
  //
  // if (isLateNight)  return 1.5;
  // if (isRushHour)   return 1.25;
  //
  // ── DB / API example ──────────────────────────────────────────────────────
  //
  // const ratio = await DemandService.getSupplyDemandRatio(origin, vehicleType);
  // if (ratio > 2) return 1.5;
  // if (ratio > 1.5) return 1.25;
  //
  return 1.0; // default: no surge
}

// ─────────────────────────────────────────────
// CORE CALCULATION
// ─────────────────────────────────────────────

/**
 * Calculate fare for a trip.
 *
 * @param {object} params
 * @param {number} params.distanceKm        - Trip distance in kilometres
 * @param {number} params.durationMinutes   - Trip duration in minutes
 * @param {string} params.vehicleType       - 'large' | 'medium' | 'small'
 * @param {boolean} [params.isRoundTrip]    - #4: double distance if true
 * @param {number} [params.surgeMultiplier] - #5: injected surge value (default 1.0)
 * @returns {object} Full fare breakdown
 */
function calculateFare({
  distanceKm,
  durationMinutes,
  vehicleType = "medium",
  isRoundTrip = false, // #4
  surgeMultiplier = 1.0, // #5
}) {
  // ── Validation ────────────────────────────────────────────────────────────
  const cfg = VEHICLE_CONFIGS[vehicleType];
  if (!cfg) {
    throw new Error(
      `Unknown vehicleType: "${vehicleType}". Valid options: ${Object.keys(VEHICLE_CONFIGS).join(" | ")}`,
    );
  }
  if (!distanceKm || distanceKm <= 0)
    throw new Error("distanceKm must be a positive number.");
  if (durationMinutes < 0)
    throw new Error("durationMinutes cannot be negative.");
  if (surgeMultiplier < 1) throw new Error("surgeMultiplier must be >= 1.0.");

  // ── #4 Round-trip: double the distance and duration ───────────────────────
  const effectiveDistance = isRoundTrip ? distanceKm * 2 : distanceKm;
  const effectiveDuration = isRoundTrip ? durationMinutes * 2 : durationMinutes;

  // ── Fixed costs per trip ──────────────────────────────────────────────────
  const vehicleDepreciationCost =
    cfg.vehicleCost /
    cfg.depreciationRatePerYear /
    12 /
    cfg.averageTripsPerMonth;

  const insuranceCost = cfg.insurancePerYear / 12 / cfg.averageTripsPerMonth;

  // ── Distance-based costs ──────────────────────────────────────────────────
  const fuelUsed = effectiveDistance / cfg.mileage;
  const fuelCost = fuelUsed * cfg.fuelPrice;

  // #2: clearly a multiplier, not a percentage
  const maintenanceCost = fuelCost * cfg.maintenanceMultiplier;
  const driverAllowance = fuelCost * cfg.driverAllowanceMultiplier;

  // ── #1 Duration-based cost ────────────────────────────────────────────────
  // Charges a per-minute rate on top of distance costs.
  // This ensures congested / slow trips are priced fairly.
  const durationCost = effectiveDuration * cfg.perMinuteRate;

  // ── Subtotal ──────────────────────────────────────────────────────────────
  const subtotal =
    fuelCost +
    vehicleDepreciationCost +
    insuranceCost +
    maintenanceCost +
    driverAllowance +
    durationCost; // #1 included here

  // ── Service charge (company revenue) ─────────────────────────────────────
  const serviceChargeAmount = subtotal * cfg.serviceChargeRate;

  // ── Time factor ───────────────────────────────────────────────────────────
  const timeFactorAddition = subtotal * (cfg.timeFactor - 1);

  // ── Raw total ─────────────────────────────────────────────────────────────
  let rawTotal = subtotal + serviceChargeAmount + timeFactorAddition;

  // ── #5 Apply surge multiplier ─────────────────────────────────────────────
  rawTotal = rawTotal * surgeMultiplier;

  // ── #3 Apply minimum fare ─────────────────────────────────────────────────
  const totalBeforeRound = Math.max(rawTotal, cfg.minimumFare);
  const minimumFareApplied =
    totalBeforeRound === cfg.minimumFare && rawTotal < cfg.minimumFare;

  // ── Round up to nearest 100 ───────────────────────────────────────────────
  const roundedTotal = Math.ceil(totalBeforeRound / 100) * 100;

  // ── Split ─────────────────────────────────────────────────────────────────
  const companyGets = +(serviceChargeAmount * surgeMultiplier).toFixed(2);
  const driverGets = roundedTotal - companyGets;

  // ── Informational ─────────────────────────────────────────────────────────
  const averageSpeedKmh =
    effectiveDuration > 0 ? (effectiveDistance / effectiveDuration) * 60 : 0;

  return {
    input: {
      distanceKm,
      durationMinutes,
      vehicleType,
      isRoundTrip,
      surgeMultiplier,
    },
    breakdown: {
      effectiveDistanceKm: +effectiveDistance.toFixed(2),
      effectiveDurationMin: +effectiveDuration.toFixed(2),
      fuelUsed: +fuelUsed.toFixed(4),
      fuelCost: +fuelCost.toFixed(2),
      vehicleDepreciationCost: +vehicleDepreciationCost.toFixed(2),
      insuranceCost: +insuranceCost.toFixed(2),
      maintenanceCost: +maintenanceCost.toFixed(2),
      driverAllowance: +driverAllowance.toFixed(2),
      durationCost: +durationCost.toFixed(2), // #1
      subtotal: +subtotal.toFixed(2),
      serviceChargeAmount: +serviceChargeAmount.toFixed(2),
      timeFactorAddition: +timeFactorAddition.toFixed(2),
      rawTotal: +rawTotal.toFixed(2),
      minimumFareApplied, // #3
      averageSpeedKmh: +averageSpeedKmh.toFixed(2),
    },
    result: {
      totalFare: roundedTotal,
      driverGets: +driverGets.toFixed(2),
      companyGets,
    },
  };
}

// ─────────────────────────────────────────────
// ROUTING HELPER
// Replace getRouteDetails() with your actual routing integration.
// ─────────────────────────────────────────────

/**
 * Stub — replace with real routing API call.
 * Should return { distanceKm, durationMinutes }.
 *
 * Example using Google Maps Directions API:
 *
 *   const axios = require('axios');
 *   const res = await axios.get('https://maps.googleapis.com/maps/api/directions/json', {
 *     params: {
 *       origin:      `${origin.lat},${origin.lng}`,
 *       destination: `${destination.lat},${destination.lng}`,
 *       key: process.env.GOOGLE_MAPS_API_KEY,
 *     }
 *   });
 *   const leg = res.data.routes[0].legs[0];
 *   return {
 *     distanceKm:      leg.distance.value / 1000,
 *     durationMinutes: leg.duration.value / 60,
 *   };
 */

const origin = { lat: 25.70548201901631, lng: 94.1064382172731 };
const destination = { lat: 25.87646967189925, lng: 93.76294513676919 };

async function getRouteDetails(origin, destination) {
  // const axios = require('axios');

  // const res = await axios.get('https://maps.googleapis.com/maps/api/directions/json', {
  //   params: {
  //     origin:      `${origin.lat},${origin.lng}`,
  //     destination: `${destination.lat},${destination.lng}`,
  //     // key: process.env.GOOGLE_MAPS_API_KEY,
  //     key:"AIzaSyDJWb2g6bYlGDSFG4r7YaF9VxGmBVIRvyk",
  //   }
  // });
  // const leg = res.data.routes[0].legs[0];

  // console.log("Route Details:", {
  //   distanceKm: leg.distance.value / 1000,
  //   durationMinutes: leg.duration.value / 60,
  // });

  const res = await axios.get("http://192.168.1.2:5000/route", {
    params: {
      origin: `${origin.lat},${origin.lng}`,
      destination: `${destination.lat},${destination.lng}`,
    },
  });
  console.log(res.data);

  return res.data;

  // return {
  //   distanceKm:      leg.distance.value / 1000,
  //   durationMinutes: leg.duration.value / 60,
  // };
  throw new Error(
    "getRouteDetails() is not implemented. " +
      "Plug in Google Maps, OSRM, Mapbox, or any routing provider.",
  );
}

// ─────────────────────────────────────────────
// MAIN ENTRY POINT
// ─────────────────────────────────────────────

/**
 * Calculate fare given two geographic points.
 *
 * @param {object} origin           - { lat: number, lng: number }
 * @param {object} destination      - { lat: number, lng: number }
 * @param {string} vehicleType      - 'large' | 'medium' | 'small'
 * @param {boolean} [isRoundTrip]   - #4: set true for return journeys
 * @param {Date}   [bookingTime]    - #5: used for surge calculation (defaults to now)
 * @returns {Promise<object>}
 *
 * @example
 * const fare = await getFareEstimate(
 *   { lat: 14.5995, lng: 120.9842 },
 *   { lat: 14.6760, lng: 121.0437 },
 *   'medium',
 *   false,
 *   new Date()
 * );
 * console.log(fare.result.totalFare);
 */
async function getFareEstimate(
  origin,
  destination,
  vehicleType = "medium",
  isRoundTrip = false,
  bookingTime = new Date(),
) {
  const { distanceKm, durationMinutes } = await getRouteDetails(
    origin,
    destination,
  );

  const surgeMultiplier = await getSurgeMultiplier({
    vehicleType,
    bookingTime,
    origin,
    destination,
  });

  return calculateFare({
    distanceKm,
    durationMinutes,
    vehicleType,
    isRoundTrip,
    surgeMultiplier,
  });
}

// ─────────────────────────────────────────────
// SELF-TESTS  (remove or move to a test suite in production)
// ─────────────────────────────────────────────
function _runTests() {
  console.log("\n── Fare Calculator v2 — Self Tests ──\n");

  let passed = 0;
  const total = 7;

  function assert(label, pass, detail = "") {
    console.log(
      `${pass ? "✅" : "❌"} ${label}${detail ? " → " + detail : ""}`,
    );
    if (pass) passed++;
  }

  // Baseline sheet checks (durationMinutes=0 to isolate original formula)
  const s1 = calculateFare({
    distanceKm: 314.86,
    durationMinutes: 0,
    vehicleType: "large",
    isRoundTrip: false,
    surgeMultiplier: 1.0,
  });
  const s2 = calculateFare({
    distanceKm: 70,
    durationMinutes: 0,
    vehicleType: "medium",
    isRoundTrip: false,
    surgeMultiplier: 1.0,
  });
  assert(
    "Sheet 1 – large baseline",
    s1.result.totalFare === 19600,
    `fare=${s1.result.totalFare}`,
  );
  assert(
    "Sheet 2 – medium baseline",
    s2.result.totalFare === 4400,
    `fare=${s2.result.totalFare}`,
  );

  // #1 Duration cost
  const d = calculateFare({
    distanceKm: 70,
    durationMinutes: 120,
    vehicleType: "medium",
    isRoundTrip: false,
    surgeMultiplier: 1.0,
  });
  assert(
    "#1 Duration cost applied",
    d.breakdown.durationCost === 120 * VEHICLE_CONFIGS.medium.perMinuteRate,
    `durationCost=${d.breakdown.durationCost}`,
  );

  // #3 Minimum fare — inject a test vehicle config with a high minimumFare
  // to guarantee rawTotal falls below it
  VEHICLE_CONFIGS.__test__ = { ...VEHICLE_CONFIGS.small, minimumFare: 999_999 };
  validateConfigs({ __test__: VEHICLE_CONFIGS.__test__ });
  const m = calculateFare({
    distanceKm: 1,
    durationMinutes: 1,
    vehicleType: "__test__",
    isRoundTrip: false,
    surgeMultiplier: 1.0,
  });
  delete VEHICLE_CONFIGS.__test__;
  assert(
    "#3 Minimum fare enforced",
    m.breakdown.minimumFareApplied === true && m.result.totalFare >= 999_999,
    `fare=${m.result.totalFare}, minFareApplied=${m.breakdown.minimumFareApplied}`,
  );

  // #4 Round trip
  const rt = calculateFare({
    distanceKm: 70,
    durationMinutes: 60,
    vehicleType: "medium",
    isRoundTrip: true,
    surgeMultiplier: 1.0,
  });
  const ow = calculateFare({
    distanceKm: 70,
    durationMinutes: 60,
    vehicleType: "medium",
    isRoundTrip: false,
    surgeMultiplier: 1.0,
  });
  assert(
    "#4 Round-trip doubles effective distance",
    rt.breakdown.effectiveDistanceKm === ow.breakdown.effectiveDistanceKm * 2,
  );

  // #5 Surge
  const surge = calculateFare({
    distanceKm: 70,
    durationMinutes: 0,
    vehicleType: "medium",
    isRoundTrip: false,
    surgeMultiplier: 1.5,
  });
  const noSurge = calculateFare({
    distanceKm: 70,
    durationMinutes: 0,
    vehicleType: "medium",
    isRoundTrip: false,
    surgeMultiplier: 1.0,
  });
  assert(
    "#5 Surge increases raw total",
    surge.breakdown.rawTotal > noSurge.breakdown.rawTotal,
    `${surge.breakdown.rawTotal} > ${noSurge.breakdown.rawTotal}`,
  );

  // #6 Config validation
  try {
    validateConfigs({ broken: { fuelPrice: "oops" } });
    assert("#6 Config validation catches bad config", false);
  } catch {
    assert("#6 Config validation catches bad config", true);
  }

  console.log(`\n${passed}/${total} tests passed.\n`);
}

_runTests();

// getFareEstimate(origin, destination, "large")
//   .then((fare) => {
//     console.log("Fare Estimate:", fare);
//   })
//   .catch((err) => {
//     console.error("Error getting fare estimate:", err.message);
//   });

// ─────────────────────────────────────────────
// EXPORTS
// ─────────────────────────────────────────────
// module.exports = {
//   calculateFare,
//   getFareEstimate,
//   getSurgeMultiplier,
//   validateConfigs,
//   VEHICLE_CONFIGS,
// };

export {
  calculateFare,
  getFareEstimate,
  getSurgeMultiplier,
  validateConfigs,
  VEHICLE_CONFIGS,
};

// #1 Duration pricing — Added perMinuteRate per vehicle type (8/5/3 per minute for large/medium/small).
// The durationCost is now included in the subtotal, so a slow 2-hour trip costs more than a fast one covering the same distance.

// #2 Naming fix — maintenanceRate → maintenanceMultiplier and driverAllowanceRate → driverAllowanceMultiplier.
//  A comment clearly states these are direct multipliers (×1.6), not percentages. This prevents a future developer from misreading 1.6 as "1.6%".

// #3 Minimum fare — Each vehicle type has a minimumFare floor. After all calculations,
//  the fare is Math.max(rawTotal, minimumFare) before rounding.The breakdown also flags minimumFareApplied: true so you can log or display it to the driver.

// #4 Round-trip — isRoundTrip: true now actually doubles both effectiveDistance and effectiveDuration before any calculations run.

// #5 Surge hook — getSurgeMultiplier() receives vehicleType, bookingTime, origin, and destination.
//  It returns 1.0 by default but has commented examples for rush-hour rules and demand/supply ratios ready to uncomment.

// #6 Config validation — validateConfigs() runs at module load time and throws a descriptive error if any field is missing, not a number,
//  or negative — so bad configs fail immediately instead of producing silent NaN fares.
